import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Save, AlertCircle, CheckCircle, Car } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rateInput, setRateInput] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      setRateInput(res.data.personal_vehicle_rate_per_km?.toString() || '10');
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setError(''); setSuccess('');
    const rate = parseFloat(rateInput);
    if (!rate || rate <= 0) {
      setError('Rate must be a positive number');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings`, { personal_vehicle_rate_per_km: rate });
      setSettings(res.data);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-gray-600">Admin access required</p>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading settings...</div></div>;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Settings className="w-5 h-5 text-gray-400" /> System Settings
        </h1>
        <p className="text-sm text-gray-500 mt-1">Configure global system parameters</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" data-testid="settings-error">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2" data-testid="settings-success">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-sm text-green-700">{success}</span>
        </div>
      )}

      <div className="card" data-testid="settings-card">
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
            <Car className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Personal Vehicle Reimbursement</h2>
            <p className="text-xs text-gray-500">Rate per kilometre for personal vehicle expenses</p>
          </div>
        </div>

        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Rate per KM (INR)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
              <input
                type="number"
                value={rateInput}
                onChange={e => setRateInput(e.target.value)}
                className="input-field pl-7"
                step="0.5"
                min="0.5"
                data-testid="rate-per-km-input"
              />
            </div>
            {settings?.updated_at && (
              <p className="text-xs text-gray-400 mt-1">Last updated: {new Date(settings.updated_at).toLocaleString()}{settings.updated_by ? ` by ${settings.updated_by}` : ''}</p>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary flex items-center gap-2"
            data-testid="save-settings-button"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="mt-6 card bg-gray-50 border-gray-200">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">How It Works</h3>
        <ul className="text-xs text-gray-500 space-y-1">
          <li>When an employee selects "Personal Vehicle" category, the amount is auto-calculated as <strong>km x rate</strong>.</li>
          <li>Changing the rate here only affects <strong>new submissions</strong>. Existing expenses keep their historical rate.</li>
          <li>Current rate: <strong>₹{settings?.personal_vehicle_rate_per_km || 10}/km</strong></li>
        </ul>
      </div>
    </div>
  );
};

export default SettingsPage;
